import React, { useState, useEffect } from 'react';
import { Button, Spinner, Container, Modal } from 'react-bootstrap';
import { FaTrash, FaUpload, FaImage, FaTimes, FaCheckSquare, FaSquare } from 'react-icons/fa';
import Masonry from 'react-masonry-css';
import { toast } from 'react-toastify';
import { handleApiResponse } from '../../modals/global/ApiMessageModal/ApiMessageModal';
import PhotoUploadModal from '../../modals/picha/PhotoUploadModal/PhotoUploadModal';
import { eventPhotosApi } from '../../api/events/eventPhotosApi';
import 'bootstrap/dist/css/bootstrap.min.css';
import './Picha.css';
import '../ecards/admin-ecards.css';

const Picha = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [photoToDelete, setPhotoToDelete] = useState(null); // single photo
  const [isDeleting, setIsDeleting] = useState(false);

  // Multi-select state
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const fetchPhotos = async (currentPage = 1) => {
    if (currentPage === 1) setLoading(true);
    else setLoadingMore(true);

    try {
      const response = await eventPhotosApi.getPhotos(currentPage);
      const responseData = response?.data || response || {};

      let imagesArray = [];
      if (Array.isArray(responseData)) {
        imagesArray = responseData;
      } else if (Array.isArray(responseData.data)) {
        imagesArray = responseData.data;
      } else if (responseData.data?.images && Array.isArray(responseData.data.images)) {
        imagesArray = responseData.data.images;
      } else if (responseData.data?.data && Array.isArray(responseData.data.data)) {
        imagesArray = responseData.data.data;
      }

      const pg = {
        current_page: responseData.current_page || responseData.data?.current_page || response.current_page || currentPage,
        total_pages: responseData.total_pages || responseData.data?.total_pages || response.total_pages || responseData.last_page || responseData.data?.last_page || response.last_page || 1
      };

      const newPhotos = Array.isArray(imagesArray) ? imagesArray : [];
      if (currentPage === 1) {
        setPhotos(newPhotos);
      } else {
        setPhotos(prev => {
          const existingIds = new Set(prev.map(i => i.id));
          return [...prev, ...newPhotos.filter(i => !existingIds.has(i.id))];
        });
      }
      setPagination(pg);
    } catch (err) {
      console.error('Error fetching photos:', err);
      toast.error('Failed to load photos.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchPhotos(1);
  }, []);

  const loadMore = () => {
    if (pagination && page < pagination.total_pages) {
      const nextPage = page + 1;
      setPage(nextPage);
      fetchPhotos(nextPage);
    }
  };

  // ── Single delete ──────────────────────────────────────────
  const confirmDelete = (photo) => {
    setPhotoToDelete(photo);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!photoToDelete?.id) return;
    setIsDeleting(true);
    try {
      const response = await eventPhotosApi.deletePhoto(photoToDelete.id);
      handleApiResponse(response);
      setShowDeleteModal(false);
      setPhotoToDelete(null);
      fetchPhotos(1);
      setPage(1);
    } catch (err) {
      console.error('Error deleting photo:', err);
      handleApiResponse(err);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Bulk delete ────────────────────────────────────────────
  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds([]);
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    setIsDeleting(true);
    try {
      await Promise.all(selectedIds.map(id => eventPhotosApi.deletePhoto(id)));
      toast.success(`${selectedIds.length} photo(s) deleted successfully!`);
      setShowBulkDeleteModal(false);
      setSelectedIds([]);
      setSelectMode(false);
      fetchPhotos(1);
      setPage(1);
    } catch (err) {
      console.error('Bulk delete error:', err);
      toast.error('Failed to delete some photos.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUploadSuccess = () => {
    setPage(1);
    fetchPhotos(1);
  };

  const breakpointColumnsObj = {
    default: 4,
    1100: 3,
    700: 2,
    500: 2
  };

  return (
    <Container className="pichaContainer bg-white">
      {/* ── Header ── */}
      <div className="pichaHeader text-center py-4">
        <h1 className="stylish-heading text-center" style={{ color: '#4169e1' }}>Picha na Kumbukumbu</h1>

        <div className="d-flex justify-content-center gap-3 mt-3 flex-wrap">
          <Button
            variant="primary"
            className="px-4 py-2 shadow"
            onClick={() => setShowModal(true)}
            style={{ borderRadius: '50px', background: 'linear-gradient(135deg, #4169e1 0%, #3a59c0 100%)', border: 'none' }}
          >
            <FaUpload className="me-2" /> Upload Photos
          </Button>

          <Button
            variant={selectMode ? 'warning' : 'outline-secondary'}
            className="px-4 py-2"
            onClick={toggleSelectMode}
            style={{ borderRadius: '50px' }}
          >
            {selectMode ? <><FaTimes className="me-2" />Cancel Selection</> : <><FaCheckSquare className="me-2" />Select to Delete</>}
          </Button>

          {selectMode && selectedIds.length > 0 && (
            <Button
              variant="danger"
              className="px-4 py-2 shadow"
              onClick={() => setShowBulkDeleteModal(true)}
              style={{ borderRadius: '50px' }}
            >
              <FaTrash className="me-2" /> Delete Selected ({selectedIds.length})
            </Button>
          )}
        </div>

        {selectMode && (
          <p className="text-muted small mt-2">
            Click on photos to select them, then press <strong>Delete Selected</strong>.
          </p>
        )}
      </div>

      {/* ── Photo Grid ── */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
          <Spinner animation="border" variant="primary" />
        </div>
      ) : (
        <div className="mt-4">
          {photos.length > 0 ? (
            <Masonry
              breakpointCols={breakpointColumnsObj}
              className="my-masonry-grid"
              columnClassName="my-masonry-grid_column"
            >
              {photos.map((photo) => {
                const isSelected = selectedIds.includes(photo.id);
                return (
                  <div
                    key={photo.id || Math.random()}
                    className="card-wrapper position-relative mb-4"
                    style={{
                      overflow: 'hidden',
                      borderRadius: '12px',
                      cursor: selectMode ? 'pointer' : 'default',
                      outline: isSelected ? '3px solid #dc3545' : 'none',
                      transition: 'outline 0.2s ease'
                    }}
                    onClick={() => selectMode && toggleSelect(photo.id)}
                  >
                    <img
                      src={photo.photo_path || photo.photo_url || photo.url || photo}
                      className="card-img w-100 shadow-sm"
                      alt="Event Photo"
                      style={{ display: 'block', pointerEvents: selectMode ? 'none' : 'auto' }}
                    />


                    {/* Selection checkbox overlay */}
                    {selectMode && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '8px',
                          left: '8px',
                          color: isSelected ? '#dc3545' : '#fff',
                          fontSize: '22px',
                          filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.6))',
                          pointerEvents: 'none'
                        }}
                      >
                        {isSelected ? <FaCheckSquare /> : <FaSquare />}
                      </div>
                    )}
                  </div>
                );
              })}
            </Masonry>
          ) : (
            <div className="col-12 text-center py-5">
              <div className="pichaEmptyState bg-light rounded shadow-sm p-5 border border-dashed">
                <FaImage className="pichaEmptyIcon text-muted mb-3" size={48} />
                <h5 className="text-muted">Hakuna picha zilizopakiwa bado.</h5>
                <p className="text-muted small">(No photos uploaded yet. Click the button above to start!)</p>
              </div>
            </div>
          )}

          {pagination && page < pagination.total_pages && (
            <div className="text-center mt-4 mb-5">
              <Button
                variant="primary"
                onClick={loadMore}
                disabled={loadingMore}
                style={{ padding: '10px 30px', borderRadius: '30px', background: 'linear-gradient(135deg, #4169e1 0%, #3a59c0 100%)', border: 'none' }}
              >
                {loadingMore ? <><Spinner size="sm" animation="border" className="me-2" /> Loading...</> : 'Load More'}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* ── Single Delete Confirmation Modal ── */}
      <Modal show={showDeleteModal} onHide={() => !isDeleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" /> Delete Photo
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          {photoToDelete && (
            <img
              src={photoToDelete.photo_path || photoToDelete.photo_url || photoToDelete.url}
              alt="To delete"
              style={{ width: '100%', maxHeight: '220px', objectFit: 'cover', borderRadius: '10px', marginBottom: '16px' }}
            />
          )}
          <p className="mb-0" style={{ fontSize: '1rem' }}>
            Are you sure you want to <strong className="text-danger">permanently delete</strong> this photo? This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-3">
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={isDeleting} style={{ borderRadius: '30px', minWidth: '120px' }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete} disabled={isDeleting} style={{ borderRadius: '30px', minWidth: '120px' }}>
            {isDeleting ? <><Spinner size="sm" animation="border" className="me-2" />Deleting...</> : <><FaTrash className="me-2" />Delete</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Bulk Delete Confirmation Modal ── */}
      <Modal show={showBulkDeleteModal} onHide={() => !isDeleting && setShowBulkDeleteModal(false)} centered>
        <Modal.Header closeButton={!isDeleting}>
          <Modal.Title className="text-danger">
            <FaTrash className="me-2" /> Delete {selectedIds.length} Photos
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-4">
          <div style={{ fontSize: '3rem', color: '#dc3545', marginBottom: '12px' }}>
            <FaTrash />
          </div>
          <p style={{ fontSize: '1rem' }}>
            You are about to <strong className="text-danger">permanently delete {selectedIds.length} selected photos</strong>. This action cannot be undone.
          </p>
        </Modal.Body>
        <Modal.Footer className="justify-content-center gap-3">
          <Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)} disabled={isDeleting} style={{ borderRadius: '30px', minWidth: '120px' }}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleBulkDelete} disabled={isDeleting} style={{ borderRadius: '30px', minWidth: '160px' }}>
            {isDeleting ? <><Spinner size="sm" animation="border" className="me-2" />Deleting...</> : <><FaTrash className="me-2" />Delete All</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ── Upload Modal ── */}
      <PhotoUploadModal
        show={showModal}
        handleClose={() => setShowModal(false)}
        onUploadSuccess={handleUploadSuccess}
      />
    </Container>
  );
};

export default Picha;
